// src/components/FeedbackForm.js
//
// Formulario de "reportar un problema / idea / aviso" — compartido entre /reportar/
// (src/pages/reportar.js) y el widget global de feedback (FeedbackWidget.js). Antes
// vivía duplicado como JSX inline en reportar.js; se extrajo acá para que ambos
// lugares usen exactamente el mismo formulario, sin desincronizarse.
import React, { useState } from "react";
import { useAuth } from "./AuthProvider";
import { useLanguage } from "./LanguageProvider";
import { submitDevInboxEntry } from "../services/admin-service";

const REPORT_TYPES = ["bug", "idea", "aviso"];

export default function FeedbackForm({ onSubmitted }) {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [type, setType] = useState("bug");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await submitDevInboxEntry({
        type,
        text,
        authorName: profile?.displayName || "",
        uid: user?.uid || null,
      });
      setSent(true);
      setText("");
      onSubmitted?.();
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="bg-white dark:bg-brand-dark-card rounded-2xl border border-brand-gold/20 dark:border-brand-forest p-6 text-center">
        <p className="text-brand-forest dark:text-brand-parchment font-medium">{t("report.thanks")}</p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-4 text-sm text-brand-gold-dark dark:text-brand-gold hover:underline"
        >
          {t("report.sendAnother")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white dark:bg-brand-dark-card rounded-2xl border border-brand-gold/20 dark:border-brand-forest p-6 space-y-4"
    >
      <div className="flex gap-2">
        {REPORT_TYPES.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setType(opt)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              type === opt
                ? "bg-brand-forest dark:bg-brand-gold text-brand-parchment dark:text-brand-forest"
                : "bg-brand-parchment dark:bg-brand-forest/30 text-brand-forest dark:text-brand-parchment border border-brand-gold/20 dark:border-brand-forest"
            }`}
          >
            {t(`report.type_${opt}`)}
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("report.placeholder")}
        rows={5}
        className="w-full px-4 py-3 rounded-xl border border-brand-gold/30 dark:border-brand-forest bg-brand-parchment dark:bg-brand-forest/20 text-brand-forest dark:text-brand-parchment placeholder-brand-haze text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold transition"
      />
      <button
        type="submit"
        disabled={sending || !text.trim()}
        className="w-full py-2.5 px-4 bg-brand-forest hover:bg-brand-dark disabled:opacity-50 text-brand-parchment text-sm font-medium rounded-xl transition-colors"
      >
        {sending ? t("report.sending") : t("report.submit")}
      </button>
    </form>
  );
}
